import React, { useState, ChangeEvent } from "react";
import DataTable from "react-data-table-component";
import { EyeIcon, PencilSquareIcon, TrashIcon } from "@heroicons/react/24/outline";
import ExportDropdown from "./ExportDropdown";
import { MdNotifications } from "react-icons/md";
// import { exportToExcel } from "../helper/exportToExcel";
// Define the shape of your data
interface TableData {
  [key: string]: any; // Adjust based on your data structure
}

interface TableProps<T> {
  columns: Array<{
    name: string;
    selector: (row: T) => any;
    sortable?: boolean;
    width?: string; // Optional width property
    cellClass?: string; // Optional class for cell styling
  }>;
  data: T[];
  showFilter?: boolean;
  showExport?: boolean;
  actionOption?: boolean;
  ReportName?: string;
  searchLabelName?: string;
  headerColor?: string;
  excelSheetHeader?: string[];
  onEdit?: (row: T) => void;
  onDelete?: (row: T) => void;
  onView?: (row: T) => void;
  onRemainder?: (row: T) => void;
  dateOfJoinAction?: string;
}

const ReactDataTable = <T extends TableData>({
  columns,
  data,
  showFilter = false,
  excelSheetHeader,
  showExport = false,
  actionOption = true,
  ReportName,
  headerColor,
  searchLabelName,
  onView,
  onEdit,
  onDelete,
  onRemainder,
  dateOfJoinAction
}: TableProps<T>) => {
  const [filterText, setFilterText] = useState("");

  // Define custom styles for the table
  const customStyles = {
    rows: {
      style: {
        minHeight: "45px", // Set the minimum height for rows
      },
    },
    headCells: {
      style: {
        paddingLeft: "8px",
        paddingRight: "8px",
        backgroundColor: headerColor == 'green' ? "#bbf7d0" : "#f3f4f6", // Tailwind gray-100
        fontWeight: "bold",
        fontSize: "0.9rem", // Adjust font size as needed
      },
    },
    cells: {
      style: {
        paddingLeft: "8px",
        paddingRight: "8px",
        padding: "8px",
        // Additional styles can be added here
      },
    },
  };

  // Apply column-specific styles if cellClass is defined
  const getCustomColumnStyles = (columnName: string) => {
    return {
      style: {
        // Apply specific styles based on the column
        ...(columnName && { whiteSpace: "normal" }), // Example for 'id' column
      },
    };
  };

  // Create column styles based on the column width and custom classes
  const getColumnStyles = () => {
    return columns.reduce((styles, column) => {
      if (column.width) {
        styles[column.name] = getCustomColumnStyles(column.name);
      }
      return styles;
    }, {} as any);
  };

  const actions = (row: T) => {
    const showActions = dateOfJoinAction ? !!row[dateOfJoinAction] : true; // Check if the key has a valid value

    return showActions ? (
      <div className='flex justify-end space-x-2'>
        {onEdit && (
          <button
            className='text-white bg-blue-700 mr-1 hover:bg-blue-800 focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-sm w-full sm:w-auto px-2 text-center dark:bg-blue-600 dark:hover:bg-blue-700 dark:focus:ring-blue-800'
            onClick={() => onEdit(row)}
          >
            <PencilSquareIcon className='h-4 w-4 text-white' />
          </button>
        )}
        {onView && (
          <button
            className='text-gray-800 bg-white mr-1 hover:bg-gray-400 focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-sm w-full sm:w-auto px-2 text-center dark:bg-blue-600 dark:hover:bg-blue-700 dark:focus:ring-blue-800'
            onClick={() => onView(row)}
          >
            <EyeIcon className='h-4 w-4 text-gray-800' />
          </button>
        )}
        {onDelete && (
          <button
            className='text-white bg-red-600 hover:bg-red-800 focus:ring-4  focus:outline-none focus:ring-red-300 font-medium rounded-lg text-sm w-full sm:w-auto p-2 text-center dark:bg-red-600 dark:hover:bg-red-700 dark:focus:ring-red-800'
            onClick={() => onDelete(row)}
          >
            <TrashIcon className='h-4 w-4 text-white' />
          </button>
        )}
        {onRemainder && (
          <button
            className='text-white bg-blue-600 hover:bg-blue-800 focus:ring-4  focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-sm w-full sm:w-auto p-2 text-center dark:bg-blue-600 dark:hover:bg-blue-700 dark:focus:ring-blue-800'
            onClick={() => onRemainder(row)}
          >
            <MdNotifications className='h-4 w-4 text-white' />
          </button>
        )}
      </div>
    ) : null; // Hide actions if condition is not met
  };


  const actionColumn = {
    name: "Actions",
    cell: (row: T) => actions(row),
    ignoreRowClick: true,
    allowOverflow: true,
    button: true,
  };

  // Filter data based on filterText
  const filteredData = data.filter((item) =>
    Object.values(item).some((value) => String(value).toLowerCase().includes(filterText.toLowerCase())),
  );

  console.log(filteredData, 'filteredData')

  return (
    <div className='relative '>
      {showFilter && (
        <div className='max-w-lg py-4 lg:flex items-end space-x-3'>
          <div className='w-full'>
            {searchLabelName && (
              <label className='block text-sm mb-3 font-medium leading-6 text-gray-900 dark:text-gray-400'>
                {searchLabelName}
              </label>
            )}
            <input
              type='text'
              placeholder={searchLabelName ? "" : "Search..."}
              value={filterText}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setFilterText(e.target.value)}
              className='shadow-sm bg-white border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500 dark:shadow-sm-light'
            />
          </div>
          {showExport && (
            <ExportDropdown
              removetopStyle={true}
              data={filteredData ?? []}
              excelheader={excelSheetHeader as string[]}
              reportName={ReportName ?? 'Report'}
            />
          )}
        </div>
      )}

      <DataTable
        columns={actionOption ? [...columns, actionOption ? actionColumn : {}] : columns}
        data={filteredData}
        customStyles={{ ...customStyles, ...getColumnStyles() }}
        pagination
        highlightOnHover
        pointerOnHover
        // onRowDoubleClicked={(row) => { if (onEdit) onEdit(row); }}
        onRowClicked={(row) => { if (onView && onEdit || onView) onView(row); else if (onEdit) onEdit(row);  }}
      />
    </div>
  );
};

export default ReactDataTable;
